import type { CareTrajectoryPoint } from "@/lib/api/care";
import { DateTime } from "luxon";
import { useEffect, useRef } from "react";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";

interface TrajectoryChartProps {
  points: CareTrajectoryPoint[];
  periodStart: string;
  periodEnd: string;
}

// Figma: 44px month columns, labels at y10, plot y33-95, baseline y95, counts y101.
const MONTH_WIDTH = 44;
const HEIGHT = 125;
const PLOT_TOP = 33;
const PLOT_HEIGHT = 62;
const COUNTS_TOP = 101;

export function TrajectoryChart({
  points,
  periodStart,
  periodEnd,
}: TrajectoryChartProps) {
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = scroller.current;
    if (node) node.scrollLeft = node.scrollWidth;
  }, [points.length]);

  if (points.length === 0) {
    return (
      <p className="font-body text-sm text-text-secondary">
        No assessments on file yet.
      </p>
    );
  }

  const start = DateTime.fromISO(periodStart);
  const end = DateTime.fromISO(periodEnd);
  const data = points.map((point, index) => ({
    ...point,
    index,
    label: DateTime.fromFormat(point.month, "yyyy-LL").toFormat("LLL"),
  }));
  const highlighted = data.filter((point) => {
    const month = DateTime.fromFormat(point.month, "yyyy-LL");
    return month.endOf("month") >= start && month.startOf("month") <= end;
  });
  const first = highlighted[0]?.index;
  const last = highlighted.at(-1)?.index;
  const hasScores = data.some((point) => point.score !== null);
  const count = data.length;
  const columns = {
    gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
  };

  return (
    <div
      ref={scroller}
      className="-mx-4 overflow-x-auto px-4"
      style={{ scrollbarWidth: "thin" }}
    >
      <div
        className="relative"
        style={{ height: HEIGHT, width: `max(100%, ${count * MONTH_WIDTH}px)` }}
      >
        {first !== undefined && last !== undefined ? (
          <div
            className="absolute inset-y-0 rounded border border-[#e0e0e0] bg-[#f9f9f9]"
            style={{
              left: `${(first / count) * 100}%`,
              width: `${((last - first + 1) / count) * 100}%`,
            }}
          />
        ) : null}

        <div
          className="absolute inset-x-0 grid"
          style={{ ...columns, top: PLOT_TOP, height: PLOT_HEIGHT }}
        >
          {data.map((point) => (
            <div
              key={point.month}
              className="mx-auto h-full w-px bg-[#dfddd8]"
            />
          ))}
        </div>
        <div
          className="absolute inset-x-0 h-[1.5px] rounded-full bg-[#dfddd8]"
          style={{ top: PLOT_TOP + PLOT_HEIGHT - 1 }}
        />

        <div
          className="absolute inset-x-0 grid font-body text-xs leading-4 text-[#6e6b67]"
          style={{ ...columns, top: 10 }}
        >
          {data.map((point) => (
            <span key={point.month} className="text-center">
              {point.label}
            </span>
          ))}
        </div>

        <div
          className="absolute inset-x-0"
          style={{ top: PLOT_TOP, height: PLOT_HEIGHT }}
        >
          {hasScores ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 8, right: 0, bottom: 8, left: 0 }}
              >
                <XAxis
                  hide
                  dataKey="index"
                  type="number"
                  domain={[-0.5, count - 0.5]}
                />
                <YAxis hide domain={["dataMin - 1", "dataMax + 1"]} />
                <Line
                  type="linear"
                  dataKey="score"
                  stroke="#000000"
                  strokeWidth={2}
                  connectNulls
                  dot={{
                    r: 4,
                    fill: "#1f1e1d",
                    stroke: "#dadada",
                    strokeWidth: 4,
                  }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="pt-5 text-center font-body text-sm text-text-secondary">
              No assessments on file yet.
            </p>
          )}
        </div>

        <div
          className="absolute inset-x-0 grid"
          style={{ ...columns, top: COUNTS_TOP }}
        >
          {data.map((point) => (
            <div key={point.month} className="flex justify-center">
              <Counts escalations={point.escalations} flags={point.flags} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const COUNT_TEXT =
  "inline-flex h-[18px] items-center justify-center font-body text-[9px] font-bold leading-none";

function Counts({
  escalations,
  flags,
}: {
  escalations: number;
  flags: number;
}) {
  if (escalations > 0 && flags > 0) {
    return (
      <span className="inline-flex">
        <span
          className={`${COUNT_TEXT} w-[18px] rounded-l-full bg-[#f61832] text-white`}
        >
          {escalations}
        </span>
        <span
          className={`${COUNT_TEXT} w-[18px] rounded-r-full bg-[#ffceb9] text-black`}
        >
          {flags}
        </span>
      </span>
    );
  }
  if (escalations > 0) {
    return (
      <span
        className={`${COUNT_TEXT} w-[18px] rounded-full bg-[#f61832] text-white`}
      >
        {escalations}
      </span>
    );
  }
  if (flags > 0) {
    return (
      <span
        className={`${COUNT_TEXT} w-[18px] rounded-full bg-[#ffceb9] text-black`}
      >
        {flags}
      </span>
    );
  }
  return null;
}
